import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { CircularProgress, IconButton } from "@mui/material";
import WarningIcon from '@mui/icons-material/Warning';
import { red } from '@mui/material/colors';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";

export const TermsAndConditions = () => {
    const [termsList, setTermsList] = useState([]);
    const [filteredTerms, setFilteredTerms] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);

    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const [editInput, setEditInput] = useState({ title: "", content: "", version: "" });
    const [deleteTermsId, setDeleteTermsId] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        fetchTerms();
    }, []);

    useEffect(() => {
        const lowerQuery = searchQuery.toLowerCase();
        setFilteredTerms(
            termsList.filter(term => term.title.toLowerCase().includes(lowerQuery))
        );
        setPage(0);
    }, [searchQuery, termsList]);

    const fetchTerms = async () => {
        setLoading(true);
        try {
            const response = await api.get("/terms");
            if (response.data.status === 1) {
                setTermsList(response.data.terms);
            } else {
                Swal.fire("Error", "Failed to load terms.", "error");
            }
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to load terms.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditInput(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        if (!editInput.title || !editInput.content) {
            Swal.fire("Error", "Title and Content are required", "warning");
            return false;
        }
        return true;
    };

    const saveTerms = async () => {
        if (!validateForm()) return;
        setFormLoading(true);
        try {
            if (editInput.id) {
                const response = await api.put(`/terms/${editInput.id}`, editInput);
                if (response.data.status === 1) Swal.fire("Success", "Terms updated successfully", "success");
            } else {
                const response = await api.post("/terms", editInput);
                if (response.data.status === 1) Swal.fire("Success", "Terms added successfully", "success");
            }
            handleCloseModal();
            fetchTerms();
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Unexpected error occurred.", "error");
        } finally {
            setFormLoading(false);
        }
    };

    const deleteTerms = async () => {
        setFormLoading(true);
        try {
            const response = await api.delete(`/terms/${deleteTermsId}`);
            if (response.data.status === 1) {
                Swal.fire("Deleted!", response.data.msg, "success");
                handleCloseModal();
                fetchTerms();
            } else {
                Swal.fire("Error", response.data.msg, "error");
            }
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Unexpected error occurred.", "error");
        } finally {
            setFormLoading(false);
        }
    };

    const handleCloseModal = () => {
        setOpenAddModal(false);
        setOpenEditModal(false);
        setOpenDeleteModal(false);
        setEditInput({ title: "", content: "", version: "" });
        setDeleteTermsId(null);
    };

    const handlePageChange = (event, newPage) => setPage(newPage);
    const handleRowsPerPageChange = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredTerms.slice(start, start + rowsPerPage);
    }, [filteredTerms, page, rowsPerPage]);

    const columns = useMemo(() => [
        { Header: "S.No", accessor: "s_no", align: "center", Cell: ({ row }) => page * rowsPerPage + row.index + 1 },
        { Header: "Title", accessor: "title", align: "left" },
        { Header: "Version", accessor: "version", align: "center" },
        {
            Header: "Options",
            accessor: "options",
            align: "center",
            Cell: ({ row }) => (
                <MDBox display="flex" justifyContent="center" gap={1}>
                    <MDButton color="info" onClick={() => { setEditInput(row.original); setOpenEditModal(true); }}>Edit</MDButton>
                    <MDButton color="error" onClick={() => { setDeleteTermsId(row.original.id); setOpenDeleteModal(true); }}>Delete</MDButton>
                </MDBox>
            )
        }
    ], [page, rowsPerPage]);

    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>
            <DashboardNavbar />

            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info" display="flex" justifyContent="space-between" alignItems="center">
                                <MDTypography variant="h6" color="white">Terms & Conditions</MDTypography>
                                <MDButton color="success" onClick={() => setOpenAddModal(true)}>Add Terms</MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="flex-end" p={2}>
                                <TextField
                                    size="small"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                            </MDBox>

                            <MDBox p={2}>
                                {loading ? <CircularProgress /> :
                                    <DataTable
                                        table={{ columns, rows: paginatedData }}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20] }}
                                        showTotalEntries
                                        noEndBorder
                                        pagination={{
                                            page,
                                            rowsPerPage,
                                            onPageChange: handlePageChange,
                                            onRowsPerPageChange: handleRowsPerPageChange,
                                            count: filteredTerms.length,
                                        }}
                                    />}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>

                {/* Add/Edit Modal */}
                <Dialog open={openAddModal || openEditModal} onClose={handleCloseModal} fullWidth maxWidth="md">
                    <DialogTitle>{editInput.id ? "Edit Terms" : "Add Terms"}</DialogTitle>
                    <MDBox component="form">
                        <DialogContent dividers>
                            <MDBox display="flex" flexDirection="column" gap={2}>
                                <TextField label="Title" name="title" value={editInput.title || ""} onChange={handleInputChange} fullWidth required />
                                <TextField label="Content" name="content" value={editInput.content || ""} onChange={handleInputChange} fullWidth multiline minRows={8} required />
                                <TextField label="Version" name="version" value={editInput.version || ""} onChange={handleInputChange} fullWidth />
                            </MDBox>
                        </DialogContent>
                        <DialogActions>
                            <MDButton onClick={handleCloseModal} color="secondary">Cancel</MDButton>
                            <MDButton color="success" onClick={saveTerms} disabled={formLoading}>{formLoading ? <CircularProgress size={24} /> : editInput.id ? "Update" : "Add"}</MDButton>
                        </DialogActions>
                    </MDBox>
                </Dialog>

                {/* Delete Modal */}
                <Dialog open={openDeleteModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <MDBox display="flex" alignItems="center" gap={2}>
                            <WarningIcon sx={{ color: red[700], fontSize: 40 }} />
                            <MDTypography variant="h6">Confirm Delete</MDTypography>
                        </MDBox>
                    </DialogTitle>
                    <DialogContent dividers>
                        <MDBox p={2} sx={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
                            <MDTypography>Are you sure you want to delete this Terms & Conditions? This action cannot be undone.</MDTypography>
                        </MDBox>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={handleCloseModal} sx={{ color: "gray" }}>Cancel</MDButton>
                        <MDButton color="error" onClick={deleteTerms} disabled={formLoading}>{formLoading ? <CircularProgress size={24} /> : "Delete"}</MDButton>
                    </DialogActions>
                </Dialog>
            </MDBox>
        </DashboardLayout>
    );
};

export default TermsAndConditions;
